<template>
  <gg-card>
    <gg-card-text class="pa-4">
      <div class="body-1 mb-1 kvm-prov__title">
        <v-icon size="24" color="primary" class="pb-1" aria-hidden="true">mdi-rocket-launch</v-icon>
        <span class="ml-2">Set up your server</span>
      </div>
      <p class="mb-4 kvm-prov__lead">
        Choose an operating system or a ready-made application.
        We'll build your server right away.
      </p>

      <div v-if="loading" class="py-8 text-center" role="status" aria-live="polite">
        <v-progress-circular indeterminate color="primary" size="36" width="3" aria-hidden="true" />
        <span class="kvm-sr-only">Loading setup options…</span>
      </div>

      <v-alert v-else-if="error" type="error" border="left" prominent class="mb-3" role="alert">
        {{ error.message || 'The setup options could not be loaded.' }}
        <div class="mt-2">
          <gg-btn small tile color="white" class="error--text" @click="loadCatalog">
            <v-icon left small>mdi-refresh</v-icon>Retry
          </gg-btn>
        </div>
      </v-alert>

      <template v-else>
          <!-- Region is fixed by billing (read-only). -->
          <div class="subtitle-2 mb-1">Region</div>
          <div class="mb-4">
            <v-icon small class="pb-1">mdi-map-marker</v-icon>
            {{ region || 'Set by your plan' }}
          </div>

          <!-- Operating system OR application -->
          <div class="subtitle-2 mb-2">Operating system / application</div>
          <kvm-catalog-picker
            :operating-systems="catalog.operating_systems"
            :apps="catalog.apps"
            v-model="selection"
            confirm-label="Use this"
            class="mb-4"
          />

          <!-- Domain — required when installing an app (app URL / SSL / cloud-init). -->
          <div class="subtitle-2 mb-2 mt-4">
            Domain <span class="kvm-prov__muted">{{ domainRequired ? '(required for app installs)' : '(optional)' }}</span>
          </div>
          <div class="d-flex align-start kvm-prov__domain" style="max-width: 560px;">
            <v-text-field
              v-model="domainInput"
              placeholder="app.example.com"
              outlined dense persistent-hint
              :hint="domainHint"
              :error="domainRequired && !domainInput.trim()"
              :aria-required="domainRequired ? 'true' : 'false'"
              aria-label="Domain"
              class="mr-2"
            />
            <gg-btn small tile outlined color="primary" class="mt-1" v-if="suggestedDomain" aria-label="Generate a free subdomain" @click="generateDomain">
              <v-icon left small aria-hidden="true">mdi-auto-fix</v-icon>Generate
            </gg-btn>
          </div>
          <kvm-domain-check :entitlement-id="entitlementId" :domain="domainInput" class="mt-1 mb-4" />

          <!-- SSH keys to install on the new server -->
          <v-divider class="mb-3" />
          <kvm-ssh-keys :entitlement-id="entitlementId" selectable v-model="sshKeyIds" class="mb-4" />

          <gg-btn tile color="primary" class="kvm-prov__submit" :loading="submitting" :disabled="!canSubmit" :aria-busy="submitting ? 'true' : 'false'" @click="submit">
            <v-icon left small aria-hidden="true">mdi-check</v-icon>Create server
          </gg-btn>
        </template>
    </gg-card-text>
  </gg-card>
</template>

<script>
import { mapActions } from 'vuex'
import kvm from '@/api/kvm'
import KvmSshKeys from '@/views/services/hosting/kvm/KvmSshKeys'
import KvmCatalogPicker from '@/views/services/hosting/kvm/KvmCatalogPicker'
import KvmDomainCheck from '@/views/services/hosting/kvm/KvmDomainCheck'

export default {
  name: 'HostingKvmProvision',
  components: { KvmSshKeys, KvmCatalogPicker, KvmDomainCheck },
  props: {
    entitlementId: { type: [String, Number], required: true },
    // Fixed by billing; shown read-only (region is not part of the catalog).
    region: { type: String, default: '' },
  },
  data: () => ({
    loading: true,
    submitting: false,
    error: null,
    catalog: { operating_systems: [], apps: [] },
    selection: null, // { kind: 'os'|'app', slug, options } from the catalog picker
    sshKeyIds: [], // which of the customer's keys to inject (all selected by default)
    domainInput: '', // domain override; required for apps, optional for a bare OS
    // The no-ip hostname the API offers for this service, or '' when it offers
    // none — because the service already has a domain, there is no IP yet, or the
    // environment has no no-ip domain configured. Server-side rather than derived
    // here, so the domain is configurable per environment. @since GG-994
    suggestedDomain: '',
  }),
  computed: {
    // Apps need a domain (app URL, SSL, cloud-init); a bare OS doesn't.
    domainRequired () {
      return !!(this.selection && this.selection.kind === 'app')
    },
    // Only point at Generate when the API actually offered a subdomain (the button
    // is hidden otherwise).
    domainHint () {
      const generate = this.suggestedDomain ? ' Click Generate for a free subdomain.' : ''
      return this.domainRequired
        ? `Required for app installs — used for the app URL, SSL and cloud-init.${generate}`
        : `Used for app URLs and cloud-init. Leave blank to use the default.${generate}`
    },
    canSubmit () {
      if (this.submitting || !this.selection) return false
      if (this.domainRequired && !this.domainInput.trim()) return false
      return true
    },
  },
  methods: {
    ...mapActions(['reportError']),
    // Fill the domain with the free subdomain the API offered.
    generateDomain () {
      if (!this.suggestedDomain) {
        this.reportError(new Error('A free subdomain isn’t available yet — try again in a moment.'))
        return
      }
      this.domainInput = this.suggestedDomain
    },
    // The API supplies the offer (suggested_domain), derived from the reserved IP
    // so it is available before the VM exists. Nothing is derived client-side: the
    // no-ip domain is configured per environment on the backend.
    async fetchSuggestedDomain () {
      try {
        const s = await kvm.getService(this.entitlementId)
        this.suggestedDomain = (s && s.suggested_domain) || ''
      } catch (e) { /* Generate stays hidden until the API offers one */ }
    },
    async loadCatalog () {
      try {
        this.loading = true
        this.error = null
        const data = await kvm.getCatalog(this.entitlementId)
        this.catalog = {
          operating_systems: data.operating_systems || [],
          apps: data.apps || [],
        }
      } catch (e) {
        this.error = e
      } finally {
        this.loading = false
      }
    },
    async submit () {
      const s = this.selection
      const choice = s.kind === 'os' ? { os: s.slug } : { app: s.slug }
      if (s.options && Object.keys(s.options).length) choice.options = s.options
      if (this.domainInput.trim()) choice.domain = this.domainInput.trim()
      choice.ssh_key_ids = this.sshKeyIds
      try {
        this.submitting = true
        await kvm.provision(this.entitlementId, choice)
        this.$emit('provisioning')
      } catch (e) {
        // 409 = already provisioned (e.g. a double-click); treat as "build started".
        if (e.status === 409) {
          this.$emit('provisioning')
          return
        }
        this.reportError(e)
      } finally {
        this.submitting = false
      }
    },
  },
  created () {
    this.loadCatalog()
    this.fetchSuggestedDomain()
  },
}
</script>

<style scoped lang="scss">
@import './kvm-ui';

.kvm-prov__title {
  // Styled as a card title (no h1 precedes it on the page, so it is not a
  // heading element — avoids a skipped heading level); keep the body-1 rhythm.
  margin-top: 0;
}

// 'Create server' is a filled primary action: deepen the brand green so the
// white label reaches AA (white on #0a7c3c = 5.3:1; brand #0ca750 fails 3.15:1).
.kvm-prov__submit {
  @include kvm-btn-primary-aa;
}

.kvm-prov__lead,
.kvm-prov__muted {
  @include kvm-secondary-text;
}

.kvm-sr-only {
  @include kvm-sr-only;
}

// Lift Vuetify's faint hint text on the domain field to AA secondary contrast.
.kvm-prov__domain ::v-deep .v-messages__message {
  @include kvm-secondary-text;
}

// Stronger-than-global focus ring for the actions inside this card (scoped).
::v-deep .v-btn:focus-visible {
  @include kvm-focus-ring;
}

@media (max-width: $kvm-bp-mobile) {
  .kvm-prov__submit {
    min-height: 44px;
  }
}
</style>
